import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/ContactSerene.css";

const FORM_CONFIG = {
  serene: {
    endpoint: "https://dezainally.com/vessella-backend/contact-serene.php",
    namePlaceholder: "Full Name",
  },
  palms: {
    endpoint: "https://dezainally.com/vessella-backend/contact-palms.php",
    namePlaceholder: "Full Name",
  },
  meadows: {
    endpoint: "https://dezainally.com/vessella-backend/contact-meadows.php",
    namePlaceholder: "Full Name",
  },
  woods: {
    endpoint: "https://dezainally.com/vessella-backend/contact-woods.php",
    namePlaceholder: "Full Name",
  },
  villas: {
    endpoint: "https://dezainally.com/vessella-backend/contact-villas.php",
    namePlaceholder: "Full Name",
  },
  "cyber-meadows": {
    endpoint: "https://dezainally.com/vessella-backend/contact-cyber-meadows.php",
    namePlaceholder: "Full Name",
  },
};

const defaultFadeUp = {
  hidden: { opacity: 0, y: 45, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

const defaultWordContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.06,
    },
  },
};

const defaultWordVariant = {
  hidden: { y: 35, opacity: 0, filter: "blur(6px)" },
  show: {
    y: 0,
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const line1 = "Begin Your Journey".split(" ");
const line2 = "Today".split(" ");

export default function ContactFormContainer({
  variant = "serene",
  fadeUp = defaultFadeUp,
  wordContainer = defaultWordContainer,
  wordVariant = defaultWordVariant,
}) {
  const config = useMemo(() => FORM_CONFIG[variant] ?? FORM_CONFIG.serene, [variant]);

  const [formData, setFormData] = useState({
    firstName: "",
    phone: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  useEffect(() => {
    setFormData({
      firstName: "",
      phone: "",
      email: "",
      message: "",
    });
    setErrors({});
    setTouched({});
    setStatus({
      submitting: false,
      success: false,
      error: null,
    });
  }, [variant]);

  const validateField = (name, value) => {
    const trimmedValue = value.trim();

    if (name === "firstName") {
      if (!trimmedValue) return "Name is required.";
      if (trimmedValue.length < 2) return "Name must be at least 2 characters.";
      if (!/^[a-zA-Z\s.'-]+$/.test(trimmedValue)) {
        return "Name can contain only letters.";
      }
    }

    if (name === "phone") {
      if (!trimmedValue) return "Phone number is required.";
      const digitsOnly = trimmedValue.replace(/\D/g, "");
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        return "Enter a valid phone number.";
      }
    }

    if (name === "email") {
      if (!trimmedValue) return "Email is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmedValue)) {
        return "Enter a valid email address.";
      }
    }

    if (name === "message") {
      if (!trimmedValue) return "Message is required.";
      if (trimmedValue.length < 10) return "Message must be at least 10 characters.";
    }

    return "";
  };

  const validateForm = (values) => {
    const nextErrors = {};

    Object.keys(values).forEach((field) => {
      const errorMessage = validateField(field, values[field]);
      if (errorMessage) {
        nextErrors[field] = errorMessage;
      }
    });

    return nextErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (status.success || status.error) {
      setStatus((prev) => ({ ...prev, success: false, error: null }));
    }

    if (touched[name]) {
      const fieldError = validateField(name, value);
      setErrors((prev) => {
        const nextErrors = { ...prev };
        if (fieldError) {
          nextErrors[name] = fieldError;
        } else {
          delete nextErrors[name];
        }
        return nextErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldError = validateField(name, value);
    setErrors((prev) => {
      const nextErrors = { ...prev };
      if (fieldError) {
        nextErrors[name] = fieldError;
      } else {
        delete nextErrors[name];
      }
      return nextErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitTouched = {
      firstName: true,
      phone: true,
      email: true,
      message: true,
    };
    setTouched(submitTouched);

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setStatus({
        submitting: false,
        success: false,
        error: "Please fix the highlighted fields.",
      });
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key].trim());
    });

    try {
      const response = await fetch(config.endpoint, {
        method: "POST",
        body: data,
      });

      const result = await response.json();
      if (result.status === "success") {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({ firstName: "", phone: "", email: "", message: "" });
        setErrors({});
        setTouched({});
      } else {
        setStatus({
          submitting: false,
          success: false,
          error: result.message || "Submission failed",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({
        submitting: false,
        success: false,
        error: "Network error. Please try again later.",
      });
    }
  };

  return (
    <>
      <AnimatePresence>
        {status.submitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              zIndex: 10000,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              className="spinner-border text-white mb-3"
              style={{ width: "3.5rem", height: "3.5rem" }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 className="text-white fw-light m-0">Submitting your Message...</h4>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="row g-4 g-md-5">
        <div className="col-lg-6">
          <motion.h2 className="contact-titles display-4 mb-4" variants={wordContainer}>
            {line1.map((word, i) => (
              <motion.span
                key={`line1-${i}`}
                variants={wordVariant}
                style={{
                  display: "inline-block",
                  marginRight: "0.35em",
                }}
              >
                {word}
              </motion.span>
            ))}
            <br />
            {line2.map((word, i) => (
              <motion.span
                key={`line2-${i}`}
                variants={wordVariant}
                style={{
                  display: "inline-block",
                  marginRight: "0.35em",
                }}
              >
                {word}
              </motion.span>
            ))}
          </motion.h2>

          <motion.p className="contact-text" variants={fadeUp}>
            Share your details and help us understand your requirements.
            Explore thoughtfully crafted homes designed for refined living.
            Receive personalized assistance from our experienced team.
            We'll get in touch to guide you every step of the way.
          </motion.p>
        </div>

        <div className="col-lg-6">
          <motion.form className="contact-form" variants={fadeUp} onSubmit={handleSubmit} noValidate>
            <motion.div className="mb-3" variants={fadeUp}>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="name"
                className={`form-control ${touched.firstName && errors.firstName ? "is-invalid" : ""}`}
                placeholder={config.namePlaceholder}
                required
                aria-invalid={touched.firstName && !!errors.firstName}
              />
              {touched.firstName && errors.firstName && (
                <small className="text-danger d-block mt-1">{errors.firstName}</small>
              )}
            </motion.div>

            <motion.div className="mb-3" variants={fadeUp}>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="tel"
                className={`form-control ${touched.phone && errors.phone ? "is-invalid" : ""}`}
                placeholder="+91 (0000) 000-000"
                inputMode="tel"
                required
                aria-invalid={touched.phone && !!errors.phone}
              />
              {touched.phone && errors.phone && (
                <small className="text-danger d-block mt-1">{errors.phone}</small>
              )}
            </motion.div>

            <motion.div className="mb-3" variants={fadeUp}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="email"
                className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
                placeholder="Your email"
                required
                aria-invalid={touched.email && !!errors.email}
              />
              {touched.email && errors.email && (
                <small className="text-danger d-block mt-1">{errors.email}</small>
              )}
            </motion.div>

            <motion.div className="mb-4" variants={fadeUp}>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control ${touched.message && errors.message ? "is-invalid" : ""}`}
                placeholder="Your message"
                rows="4"
                required
                aria-invalid={touched.message && !!errors.message}
              />
              {touched.message && errors.message && (
                <small className="text-danger d-block mt-1">{errors.message}</small>
              )}
            </motion.div>

            {status.success && (
              <div className="alert alert-success mt-3 mb-3 text-success">
                Message sent successfully!
              </div>
            )}
            {status.error && (
              <div className="alert alert-danger mt-3 mb-3 text-danger">{status.error}</div>
            )}

            <motion.button
              type="submit"
              className="location-btn"
              variants={fadeUp}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              disabled={status.submitting}
            >
              {status.submitting ? "Sending..." : "Send Message"}
              <i className="fa-solid fa-arrow-right ms-2"></i>
            </motion.button>
          </motion.form>
        </div>
      </div>
    </>
  );
}
